// src/pages/LMS/Students/StudentEnrollments.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  LinearProgress,
  CircularProgress,
  Alert,
  Avatar,
  Stack,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  School as CourseIcon,
  TrendingUp as ProgressIcon,
  CheckCircle as CompletedIcon,
  Schedule as InProgressIcon,
  Cancel as DroppedIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  EmojiEvents as TrophyIcon,
  Timer as TimerIcon
} from '@mui/icons-material';
import axiosInstance from '../../../../utils/axiosInstance';

const StudentEnrollments = () => {
  const { chatbotId, studentId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [student, setStudent] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchStudentEnrollments();
  }, [studentId, statusFilter]);

  const fetchStudentEnrollments = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      if (statusFilter) params.status = statusFilter;

      const response = await axiosInstance.get(
        `/api/lms/students/${studentId}/enrollments`,
        { params }
      );

      console.log('Enrollments response:', response.data);

      if (response.data.success) {
        setEnrollments(response.data.data || []);
        
        // Get student info from first enrollment if available
        if (response.data.data && response.data.data.length > 0) {
          setStudent({
            id: studentId,
            name: response.data.data[0].student?.name || 
                  `${response.data.data[0].student?.first_name || ''} ${response.data.data[0].student?.last_name || ''}`.trim(),
            email: response.data.data[0].student?.email
          });
        } else {
          // Fetch student info separately if no enrollments
          fetchStudentInfo();
        }
      } else {
        setError(response.data.message || 'Failed to load enrollments');
      }
    } catch (err) {
      console.error('Error fetching enrollments:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load enrollments');
    } finally {
      setLoading(false);
    }
  };

  const fetchStudentInfo = async () => {
    try {
      const response = await axiosInstance.get(`/api/lms/students/${studentId}`);
      if (response.data.success) {
        const studentData = response.data.data;
        setStudent({
          id: studentData.id,
          name: studentData.first_name && studentData.last_name 
            ? `${studentData.first_name} ${studentData.last_name}` 
            : studentData.email,
          email: studentData.email
        });
      }
    } catch (err) {
      console.error('Error fetching student:', err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'info';
      case 'enrolled':
        return 'default';
      case 'dropped':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CompletedIcon />;
      case 'in_progress':
        return <InProgressIcon />;
      case 'dropped':
        return <DroppedIcon />;
      default:
        return <CourseIcon />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0h';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  const getFilteredEnrollments = () => {
    if (!statusFilter) return enrollments;
    return enrollments.filter(e => e.status === statusFilter);
  };

  const getEnrollmentStats = () => {
    const stats = {
      total: enrollments.length,
      active: enrollments.filter(e => e.status === 'in_progress' || e.status === 'enrolled').length,
      completed: enrollments.filter(e => e.status === 'completed').length,
      dropped: enrollments.filter(e => e.status === 'dropped').length
    };
    return stats;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 14, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 14, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(`/chatbot/${chatbotId}/lms/students`)}
        >
          Back to Students
        </Button>
      </Container>
    );
  }

  const stats = getEnrollmentStats();
  const filteredEnrollments = getFilteredEnrollments();

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 4, sm: 14 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(`/chatbot/${chatbotId}/lms/students`)}
          sx={{ mb: 2 }}
        >
          Back to Students
        </Button>

        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Avatar sx={{ width: { xs: 48, sm: 64 }, height: { xs: 48, sm: 64 }, bgcolor: 'primary.main', fontSize: { xs: 20, sm: 28 } }}>
              {student?.name?.[0]?.toUpperCase() || 'S'}
            </Avatar>
            <Box flexGrow={1}>
              <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                {student?.name || 'Student'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {student?.email || ''}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="primary">
                {stats.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Enrollments
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="info.main">
                {stats.active}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="success.main">
                {stats.completed}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Completed
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="error.main">
                {stats.dropped}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Dropped
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filter Chips */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Chip
            label="All"
            onClick={() => setStatusFilter('')}
            color={statusFilter === '' ? 'primary' : 'default'}
            variant={statusFilter === '' ? 'filled' : 'outlined'}
          />
          <Chip
            label="Active"
            onClick={() => setStatusFilter('in_progress')}
            color={statusFilter === 'in_progress' ? 'info' : 'default'}
            variant={statusFilter === 'in_progress' ? 'filled' : 'outlined'}
          />
          <Chip
            label="Completed"
            onClick={() => setStatusFilter('completed')}
            color={statusFilter === 'completed' ? 'success' : 'default'}
            variant={statusFilter === 'completed' ? 'filled' : 'outlined'}
          />
          <Chip
            label="Dropped"
            onClick={() => setStatusFilter('dropped')}
            color={statusFilter === 'dropped' ? 'error' : 'default'}
            variant={statusFilter === 'dropped' ? 'filled' : 'outlined'}
          />
        </Stack>
      </Paper>

      {/* Enrollments Grid */}
      {filteredEnrollments.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No enrollments found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {statusFilter 
              ? 'Try changing the filter'
              : 'This student has not been enrolled in any courses yet'}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredEnrollments.map((enrollment) => (
            <Grid item xs={12} md={6} key={enrollment.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  opacity: enrollment.status === 'dropped' ? 0.7 : 1,
                  border: enrollment.status === 'completed' ? 2 : 0,
                  borderColor: 'success.main'
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  {/* Course Title */}
                  <Box display="flex" alignItems="flex-start" gap={1} mb={2}>
                    {getStatusIcon(enrollment.status)}
                    <Box flexGrow={1}>
                      <Typography variant="h6" gutterBottom>
                        {enrollment.course?.title || 'Unknown Course'}
                      </Typography>
                      <Chip
                        label={enrollment.status.replace('_', ' ').toUpperCase()}
                        color={getStatusColor(enrollment.status)}
                        size="small"
                      />
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Progress */}
                  <Box mb={2}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2" color="text.secondary">
                        Progress
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {enrollment.progress_percentage?.toFixed(0) || 0}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={enrollment.progress_percentage || 0}
                      sx={{ height: 8, borderRadius: 1 }}
                    />
                  </Box>

                  {/* Stats */}
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Stack spacing={0.5}>
                        <Typography variant="caption" color="text.secondary">
                          Modules
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {enrollment.modules_completed || 0} / {enrollment.total_modules || 0}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={6}>
                      <Stack spacing={0.5}>
                        <Typography variant="caption" color="text.secondary">
                          Tests Passed
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {enrollment.tests_passed || 0}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={6}>
                      <Stack spacing={0.5}>
                        <Typography variant="caption" color="text.secondary">
                          Points Earned
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {enrollment.points_earned || 0}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={6}>
                      <Stack spacing={0.5}>
                        <Typography variant="caption" color="text.secondary">
                          Time Spent
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {formatDuration(enrollment.total_time_spent)}
                        </Typography>
                      </Stack>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  {/* Dates */}
                  <Stack spacing={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CalendarIcon fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        Enrolled: {formatDate(enrollment.enrolled_at)}
                      </Typography>
                    </Box>
                    {enrollment.completed_at && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <CompletedIcon fontSize="small" color="success" />
                        <Typography variant="caption" color="text.secondary">
                          Completed: {formatDate(enrollment.completed_at)}
                        </Typography>
                      </Box>
                    )}
                    {enrollment.last_accessed && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <TimerIcon fontSize="small" color="action" />
                        <Typography variant="caption" color="text.secondary">
                          Last accessed: {formatDate(enrollment.last_accessed)}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </CardContent>

                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<ProgressIcon />}
                    onClick={() => navigate(
                      `/chatbot/${chatbotId}/lms/enrollments/${enrollment.id}/progress`
                    )}
                  >
                    View Progress
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default StudentEnrollments;